import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, MapPin } from "lucide-react";
import { GRAFFITI_HERO } from "../lib/api";
import { useContent } from "../lib/content";

const lineReveal = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 + i * 0.12 },
  }),
};

const MaskedLine = ({ index, children, className = "" }) => (
  <span className={`line-mask ${className}`}>
    <motion.span
      className="block will-change-transform"
      custom={index}
      variants={lineReveal}
      initial="hidden"
      animate="visible"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useContent();

  return (
    <section id="top" className="relative overflow-hidden h-[68svh] min-h-[520px] max-h-[700px] flex items-end">
      <img
        src={t("hero_image") || GRAFFITI_HERO}
        alt="Colorful graffiti mural wall"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-black/25" />
      <div className="absolute inset-x-0 bottom-0 h-[75%] bg-gradient-to-t from-black via-black/60 to-transparent" />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 pb-14 sm:pb-16 pt-40">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          data-testid="hero-appointment-badge"
          className="text-[11px] font-bold tracking-[0.3em] uppercase text-white/80 flex items-center gap-3"
        >
          <span className="w-8 h-px bg-[#eac119]" />
          {t("about_kicker")} · {t("hero_badge")}
        </motion.p>

        <h1
          data-testid="hero-title"
          className="mt-5 font-display font-extrabold tracking-[-0.03em] text-4xl sm:text-6xl lg:text-7xl leading-[1.02] text-white drop-shadow-[0_2px_18px_rgba(0,0,0,0.55)]"
        >
          <MaskedLine index={0}>{t("hero_title_1")}</MaskedLine>
          <MaskedLine index={1}>
            <span className="text-[#eac119]">{t("hero_title_2")}</span>
            <span className="text-white">—</span>
          </MaskedLine>
        </h1>

        <MaskedLine index={2} className="mt-5">
          <p data-testid="hero-subtitle" className="text-sm sm:text-lg text-white/90 leading-relaxed max-w-xl font-semibold drop-shadow-[0_1px_10px_rgba(0,0,0,0.6)]">
            {t("hero_subtitle")}
          </p>
        </MaskedLine>

        <MaskedLine index={3} className="mt-4">
          <p className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-white/75 drop-shadow-[0_1px_8px_rgba(0,0,0,0.6)]">
            <MapPin size={14} className="text-[#eac119] shrink-0" />
            {t("hero_meta")}
          </p>
        </MaskedLine>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 flex flex-wrap items-center gap-3 sm:gap-4"
        >
          <a
            href="#schedule"
            data-testid="hero-explore-button"
            className="btn-primary inline-flex items-center gap-2 font-bold px-7 py-3.5 sm:px-8 sm:py-4 rounded-full text-sm sm:text-[15px]"
          >
            Come Paint
            <ArrowRight size={16} />
          </a>
          <a
            href="#pottery"
            data-testid="hero-pottery-link"
            className="inline-flex items-center gap-2 font-bold px-7 py-3.5 sm:px-8 sm:py-4 rounded-full text-sm sm:text-[15px] text-white bg-black/55 backdrop-blur-md border border-white/25 hover:bg-black/75 transition-all duration-300"
          >
            Interested in Pottery?
            <ArrowUpRight size={16} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
